import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const Home = () => {
  // Schema.org Organization para SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "FuneralHome",
    "name": "Santa Casa Serviço Funerário Imediato",
    "alternateName": "Funerária Santa Casa Meier",
    "description": "Serviço funerário 24 horas no Rio de Janeiro. Atendimento imediato no Meier, Tijuca, Centro, Barra e Botafogo. Valores transparentes a partir de R$ 1.599.",
    "url": "https://funerariasantacasaassistencial.com",
    "telephone": "+55-21-4106-4022",
    "priceRange": "R$ 1.599 - R$ 4.599",
    "image": "https://funerariasantacasaassistencial.com/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Meier",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "postalCode": "20000-000",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-22.9035",
      "longitude": "-43.2096"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Rio de Janeiro",
        "containedInPlace": {
          "@type": "State",
          "name": "Rio de Janeiro"
        }
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Serviços Funerários",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Funeral Básico",
            "description": "Funeral completo com todos os serviços básicos"
          },
          "price": "1599.00",
          "priceCurrency": "BRL"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Funeral Completo",
            "description": "Sepultamento tradicional completo"
          },
          "price": "2899.00",
          "priceCurrency": "BRL"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Funeral Premium",
            "description": "Máximo conforto e dignidade"
          },
          "price": "4599.00",
          "priceCurrency": "BRL"
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "18000",
      "bestRating": "5"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Santa Casa Serviço Funerário Imediato",
    "@id": "https://funerariasantacasaassistencial.com",
    "url": "https://funerariasantacasaassistencial.com",
    "telephone": "+552141064022",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Meier",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "openingHours": "Mo-Su 00:00-23:59",
    "sameAs": [
      "https://wa.me/552141064022",
      "https://www.instagram.com/santacasa.assistencial"
    ]
  };

  const regioes = [
    {
      nome: 'Meier',
      link: '/funeraria-no-meier',
      descricao: 'Atendimento funerário 24h no coração do Meier - Zona Norte'
    },
    {
      nome: 'Tijuca',
      link: '/funeraria-na-tijuca',
      descricao: 'Serviços funerários completos e imediatos na Tijuca'
    },
    {
      nome: 'Centro',
      link: '/funeraria-no-centro',
      descricao: 'Funerária 24h no Centro do Rio com atendimento emergencial'
    },
    {
      nome: 'Barra da Tijuca',
      link: '/funeraria-barra-da-tijuca',
      descricao: 'Atendimento funerário completo na Barra - Zona Oeste'
    },
    {
      nome: 'Botafogo',
      link: '/funeraria-em-botafogo',
      descricao: 'Serviço funerário imediato em Botafogo - Zona Sul'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Schema.org JSON-LD para SEO */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(26, 188, 156, 0.95), rgba(20, 160, 133, 0.9)), url('https://images.unsplash.com/photo-1581964268095-79e51075214d?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6 border border-white/30">
            <CheckCircle className="w-5 h-5 text-lime-300 mr-2" />
            <span className="text-sm font-semibold">✨ 18.000+ Famílias Atendidas</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl">
            Funerária Rio de Janeiro 24 Horas
          </h1>
          <p className="text-xl sm:text-2xl mb-4 font-semibold drop-shadow-lg">
            Santa Casa Serviço Funerário Imediato
          </p>
          <p className="text-base md:text-lg text-teal-50 font-light drop-shadow-lg mb-8">
            Funerária RJ | Funerária 24 Horas Rio de Janeiro | Cremação | Velório | Sepultamento | Translado | Remoção de Corpo | Assistência Funeral Completa
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-lime-400 via-lime-500 to-lime-600 hover:from-lime-500 hover:via-lime-600 hover:to-lime-700 text-gray-900 px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm sm:text-base font-bold transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Ligar Agora: (21) 4106-4022</span>
            </a>
            <a
              href="https://wa.me/552141064022?text=Ol%C3%A1!%20Vim%20pelo%20site,%20gostaria%20de%20informa%C3%A7%C3%B5es"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm sm:text-base font-bold transition-all duration-300 hover:scale-105 border-2 border-white/30 shadow-xl"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-lime-300" />
              <span className="text-xs sm:text-sm font-semibold">24h Disponível</span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-lime-300" />
              <span className="text-xs sm:text-sm font-semibold">Zona Norte e Sul</span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
              <span className="text-xs sm:text-sm font-semibold">4.8/5 Avaliação</span>
            </div>
          </div>
        </div>
      </section>

      {/* Seção SEO com Palavras-Chave */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Funerária no Rio de Janeiro - Atendimento 24 Horas
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              A melhor funerária RJ com serviço funerário completo e atendimento imediato em toda região metropolitana do Rio de Janeiro
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Funerária 24 Horas no Rio de Janeiro</h3>
              <p className="text-gray-700 mb-4">
                Somos uma <strong>funerária no Rio de Janeiro</strong> especializada em <strong>atendimento funerário 24 horas</strong>. Como a mais completa <strong>funerária RJ</strong>, oferecemos serviços de <strong>cremação</strong>, <strong>sepultamento</strong>, <strong>velório</strong>, <strong>translado</strong> e <strong>remoção de corpo</strong> em todas as regiões do Rio: Zona Norte, Zona Sul, Centro e Zona Oeste.
              </p>
              <p className="text-gray-700">
                Nossa <strong>funerária 24 horas</strong> atende emergências funerárias com plantão permanente, equipe especializada e toda documentação necessária. Trabalhamos com os melhores <strong>serviços funerários Rio de Janeiro</strong> tem a oferecer, garantindo dignidade, respeito e suporte integral à família.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-teal-50 rounded-xl p-6 border-2 border-teal-200">
                <h4 className="text-xl font-bold text-teal-900 mb-3">Funerária Rio de Janeiro</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>✓ Funerária completa RJ</li>
                  <li>✓ Funerária 24 horas Rio de Janeiro</li>
                  <li>✓ Plantão funerário RJ</li>
                  <li>✓ Atendimento emergencial</li>
                  <li>✓ Serviço funerário imediato</li>
                  <li>✓ Funerária Zona Norte RJ</li>
                  <li>✓ Funerária Zona Sul RJ</li>
                </ul>
              </div>

              <div className="bg-lime-50 rounded-xl p-6 border-2 border-lime-200">
                <h4 className="text-xl font-bold text-lime-900 mb-3">Serviços Funerários RJ</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>✓ Cremação Rio de Janeiro</li>
                  <li>✓ Sepultamento RJ</li>
                  <li>✓ Velório 24h</li>
                  <li>✓ Translado funerário</li>
                  <li>✓ Remoção de corpo RJ</li>
                  <li>✓ Documentação funeral</li>
                  <li>✓ Urnas funerárias</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <h4 className="text-xl font-bold text-blue-900 mb-3">Regiões Atendidas</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>✓ Funerária Meier RJ</li>
                  <li>✓ Funerária Tijuca</li>
                  <li>✓ Funerária Centro RJ</li>
                  <li>✓ Funerária Barra da Tijuca</li>
                  <li>✓ Funerária Botafogo</li>
                  <li>✓ Toda região metropolitana</li>
                  <li>✓ Cobertura estadual</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-600 to-lime-600 text-white rounded-xl p-8 text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Funerária RJ com Mais de 18.000 Famílias Atendidas
              </h3>
              <p className="text-lg mb-6">
                A Santa Casa é a <strong>funerária no Rio de Janeiro</strong> mais confiável para quem busca <strong>serviço funerário de qualidade</strong>. Nossa <strong>funerária 24 horas</strong> oferece atendimento completo com cremação, sepultamento, velório, translado e toda assistência funeral necessária em momento tão delicado.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">24h</div>
                  <div className="text-sm">Funerária 24 Horas</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">RJ</div>
                  <div className="text-sm">Rio de Janeiro</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">5</div>
                  <div className="text-sm">Regiões Cobertas</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">18k+</div>
                  <div className="text-sm">Famílias Atendidas</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Por Que Escolher Nossa Funerária no Rio de Janeiro?</h3>
              <p className="text-gray-700 mb-6">
                Como a mais completa <strong>funerária RJ</strong>, oferecemos atendimento diferenciado com <strong>plantão funerário 24 horas</strong>, equipe especializada, documentação completa e preços transparentes. Nossa <strong>funerária Rio de Janeiro</strong> atende em Meier, Tijuca, Centro, Barra da Tijuca, Botafogo e toda região metropolitana com serviço funerário imediato.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Funerária 24 Horas:</strong>
                    <span className="text-gray-700"> Plantão funerário permanente no Rio de Janeiro</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Funerária Completa RJ:</strong>
                    <span className="text-gray-700"> Cremação, sepultamento, velório e translado</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Funerária Rio de Janeiro:</strong>
                    <span className="text-gray-700"> Cobertura em todas as regiões do RJ</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Serviço Funerário Imediato:</strong>
                    <span className="text-gray-700"> Atendimento emergencial e remoção rápida</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nossas Regiões de Atendimento */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Onde Atendemos no Rio de Janeiro
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Selecione sua região para informações específicas de atendimento
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {regioes.map((regiao, index) => (
              <Link
                key={index}
                to={regiao.link}
                className="group bg-gradient-to-br from-teal-50 to-lime-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-teal-100 hover:border-teal-500"
              >
                <MapPin className="w-12 h-12 text-teal-600 mb-4 group-hover:text-lime-600 transition-colors" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{regiao.nome}</h3>
                <p className="text-gray-600 text-sm mb-4">{regiao.descricao}</p>
                <div className="flex items-center text-teal-600 group-hover:text-lime-600 font-semibold">
                  Ver detalhes
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nossos Serviços */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Nossos Serviços Funerários
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link
              to="/cremacao"
              className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <div className="text-teal-600 mb-4">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Cremação</h3>
              <p className="text-gray-600">
                Serviços completos de cremação com toda documentação e suporte necessário
              </p>
            </Link>

            <Link
              to="/translado"
              className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <div className="text-teal-600 mb-4">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Translado</h3>
              <p className="text-gray-600">
                Translado nacional e internacional com toda segurança e cuidado
              </p>
            </Link>

            <Link
              to="/funeral"
              className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <div className="text-teal-600 mb-4">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Sepultamento</h3>
              <p className="text-gray-600">
                Organização completa de cerimônias e sepultamento
              </p>
            </Link>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center bg-teal-600 hover:bg-lime-600 text-white px-8 py-3 rounded-xl text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Entre em Contato
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Conteúdo Rico sobre Atendimento Funerário */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Atendimento Funerário Completo 24 Horas
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Serviço funerário imediato com equipe especializada, documentação completa e suporte integral
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">🚑 Atendimento Funerário Emergencial</h3>
              <p className="text-gray-700 mb-4">
                Nosso serviço de atendimento funerário está disponível 24 horas por dia, todos os dias do ano. Realizamos remoção de corpo imediata, transporte funerário, preparação do falecido e toda documentação necessária. Equipe treinada para atendimento domiciliar, hospitalar e IML.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Remoção e transporte funerário 24h</li>
                <li>✓ Preparação e tanatopraxia</li>
                <li>✓ Documentação e certidões</li>
                <li>✓ Atendimento em domicílio, hospital e IML</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Documentação Funerária Completa</h3>
              <p className="text-gray-700 mb-4">
                Cuidamos de toda burocracia: certidão de óbito, liberação do corpo, autorização do cartório, documentos para cremação ou sepultamento. Nossa equipe agiliza todo o processo para que você não precise se preocupar com papelada em momento tão delicado.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Certidão de óbito</li>
                <li>✓ Liberação hospitalar e IML</li>
                <li>✓ Registro em cartório</li>
                <li>✓ Autorizações legais</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">⚰️ Serviços Funerários Inclusos</h3>
              <p className="text-gray-700 mb-4">
                Oferecemos serviço funerário completo: urnas de diversos materiais e acabamentos, ornamentação floral, véu, paramentos, livro de presença, cafezinho e água. Capela velório equipada com ar-condicionado, banheiros, estacionamento e espaço para familiares.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Urna funerária (madeira, metal, luxo)</li>
                <li>✓ Ornamentação e flores naturais</li>
                <li>✓ Capela velório climatizada</li>
                <li>✓ Paramentos e véu</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">🕊️ Cremação e Sepultamento</h3>
              <p className="text-gray-700 mb-4">
                Serviço completo de cremação em crematórios certificados ou sepultamento em cemitérios de sua escolha. Coordenamos cerimônia religiosa ou laica, transporte até o local, cortejo fúnebre, abertura e fechamento de sepultura, e entrega de urna cinerária.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Cremação em crematórios licenciados</li>
                <li>✓ Sepultamento em cemitérios públicos/privados</li>
                <li>✓ Cerimônia religiosa ou laica</li>
                <li>✓ Cortejo e coordenação</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-600 to-lime-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Atendimento Funerário Humanizado</h3>
            <p className="text-lg mb-6">
              Mais de 18.000 famílias atendidas com respeito, empatia e profissionalismo. Nossa equipe oferece suporte emocional, orientação sobre todas as etapas do serviço funerário e acompanhamento personalizado. Atendemos todas as religiões e culturas com dedicação e sensibilidade.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold mb-2">24h</div>
                <div className="text-sm">Plantão Funerário</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">18.000+</div>
                <div className="text-sm">Famílias Atendidas</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm">Documentação Inclusa</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">5 Regiões</div>
                <div className="text-sm">Cobertura no RJ</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Final com Palavras-Chave Adicionais */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Funerária Rio de Janeiro - Serviço Funerário Completo 24 Horas
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                A Santa Casa é a <strong>funerária no Rio de Janeiro</strong> mais completa e confiável, oferecendo <strong>serviço funerário 24 horas</strong> em todas as regiões do RJ. Como a melhor <strong>funerária RJ</strong>, disponibilizamos <strong>plantão funerário permanente</strong>, <strong>atendimento emergencial</strong>, <strong>remoção de corpo imediata</strong>, <strong>velório 24h</strong>, <strong>cremação no Rio de Janeiro</strong>, <strong>sepultamento</strong> e <strong>translado funerário nacional e internacional</strong>.
              </p>

              <p className="mb-4">
                Nossa <strong>funerária 24 horas Rio de Janeiro</strong> atende com excelência em todas as regiões: <strong>funerária Meier</strong>, <strong>funerária Tijuca</strong>, <strong>funerária Centro RJ</strong>, <strong>funerária Barra da Tijuca</strong>, <strong>funerária Botafogo</strong>, <strong>funerária Zona Norte</strong>, <strong>funerária Zona Sul</strong> e toda região metropolitana. Somos especialistas em <strong>serviços funerários RJ</strong> com mais de 18.000 famílias atendidas.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded my-6">
                <h3 className="text-xl font-bold text-teal-900 mb-3">Serviços da Nossa Funerária Rio de Janeiro:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <strong>Funerária 24h RJ:</strong>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Plantão funerário 24 horas</li>
                      <li>• Atendimento emergencial RJ</li>
                      <li>• Remoção de corpo imediata</li>
                      <li>• Serviço funerário urgente</li>
                    </ul>
                  </div>
                  <div>
                    <strong>Funerária Completa:</strong>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Cremação Rio de Janeiro</li>
                      <li>• Sepultamento RJ</li>
                      <li>• Velório 24 horas</li>
                      <li>• Translado funerário</li>
                    </ul>
                  </div>
                  <div>
                    <strong>Assistência Funeral RJ:</strong>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Documentação completa</li>
                      <li>• Urnas funerárias</li>
                      <li>• Flores e ornamentação</li>
                      <li>• Cerimônia religiosa</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="mb-4">
                Como <strong>funerária no Rio de Janeiro</strong> de referência, oferecemos <strong>serviço funerário completo</strong> com documentação inclusa, <strong>velório equipado</strong>, <strong>cremação em crematórios certificados</strong>, <strong>sepultamento em cemitérios públicos e privados</strong>, <strong>translado aéreo e rodoviário</strong>, além de <strong>suporte emocional</strong> e orientação para a família. Nossa <strong>funerária 24 horas</strong> garante atendimento humanizado e profissional em qualquer hora do dia ou noite.
              </p>

              <div className="text-center mt-8 p-6 bg-gradient-to-r from-teal-50 to-lime-50 rounded-xl">
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  🏆 A Melhor Funerária Rio de Janeiro | Funerária RJ 24 Horas
                </p>
                <p className="text-gray-700">
                  <strong>Funerária completa</strong> com atendimento imediato em Meier, Tijuca, Centro, Barra, Botafogo e toda região do Rio de Janeiro. <strong>Ligue agora: (21) 4106-4022</strong> - Plantão funerário 24h, cremação, sepultamento, velório e translado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-lime-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Atendimento 24 Horas
          </h2>
          <p className="text-base md:text-lg mb-8 text-teal-50">
            Nossa equipe está sempre disponível para atendê-lo com dignidade e respeito
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center justify-center bg-white text-teal-600 hover:bg-gray-100 px-8 py-3 rounded-xl text-lg font-bold transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Ligar Agora
            </a>
            <a
              href="https://wa.me/552141064022?text=Ol%C3%A1!%20Vim%20pelo%20site,%20gostaria%20de%20informa%C3%A7%C3%B5es"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-xl text-lg font-bold transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
